/**
 * Mapa da Produção (Legado) — a fábrica vista de cima, ao vivo.
 *
 * Cada SETOR CADASTRADO é uma sala, desenhada com a forma que o gestor riscou
 * no mapa; dentro dela, os subsetores (áreas), os postos (bancadas) com as
 * pessoas, os ambientes e as PEÇAS DESMEMBRADAS deles. A ETAPA de cada ambiente
 * vem da mesma planilha do Legado (`setores.js` + `dados/etapasPlanilha.js`:
 * data preenchida = etapa finalizada) e é o vínculo etapa → setor que decide em
 * qual sala o ambiente cai. O que o gestor aponta no app — setor, subsetor,
 * posto, status, prazo, tarefas, peças — vem do estado do backend, lido a cada
 * 15 s (useEstadoMapa), junto com o desenho.
 *
 * Ambiente cuja etapa não tem nenhuma sala desenhada não desaparece: vai para a
 * bandeja "Ainda no escritório" (ou "Sem sala no mapa"), agrupado por etapa, com o atalho para criar a
 * sala que falta.
 *
 * Esta tela só LÊ o trabalho (apontar é na página do setor) e ESCREVE o
 * desenho: no modo Organizar, quem tem `mapa_producao:gerir` desenha uma sala
 * nova com o dedo, redesenha, move, redimensiona, renomeia e troca a etapa.
 * (Na v2 o desenho tinha permissão própria, `mapa_producao:editar_mapa`; ela
 * saiu do catálogo junto com o /layout — permissoes/migrations/0041 — porque a
 * forma virou campo do cadastro do setor, que já é `gerir`.)
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEdit3, FiPenTool, FiRotateCcw, FiSearch } from "react-icons/fi";

import AmbienteDetalhesDrawer from "../../components/AmbienteDetalhesDrawer";
import AsyncButton from "../../components/AsyncButton";
import { EmptyState } from "../../components/EmptyState";
import { PageLoader } from "../../components/LoadingMarcenaria";
import PageHeader from "../../components/PageHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useDados } from "../../context/DadosContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import { plural } from "../../utils/dinheiro";
import { F } from "../../utils/gerencialFields";
import { PrimaryButton, SecondaryButton } from "../Orcamentos/styles";
import { indexarEstado } from "./dados/combinar";
import { criarFiltrosVazios } from "./dados/filtros";
import { pecasForaDoAmbiente } from "./dados/pecas";
import useEstadoMapa from "./dados/useEstadoMapa";
import useFiltrosDoMapa from "./dados/useFiltrosDoMapa";
import { PERMISSAO } from "./dados/vocabulario";
import PainelDeOrfaos, { AvisoDeOrfaos, useAmbientesOrfaos } from "./ConciliarAmbientes";
import ExportarApontamentos from "./ExportarApontamentos";
import FaixaIndicadores, { Vivo } from "./FaixaIndicadores";
import FiltrosDoMapa, { ChipsDosFiltros } from "./FiltrosDoMapa";
import FormularioSetor from "./FormularioSetor";
import { elementosParaDesenho, formaParaNovoSetor, formasAlteradas, levarFilhos } from "./layout";
import ListaDeSetores from "./ListaDeSetores";
import ListaDemandas, { CartaoDemanda } from "./ListaDemandas";
import MapaSvg from "./MapaSvg";
import PopoverMapa, { ConteudoAmbiente, ConteudoMais, ConteudoPeca, ConteudoPessoa } from "./PopoverMapa";
import { SITUACAO, criarCatalogoSetores, gruposDaBandeja } from "./setores";
import {
  ApontamentosSoIcone,
  AvisoErro,
  AvisoOrganizar,
  AvisosSobreOMapa,
  BarraDeSelecao,
  Barra,
  BotaoBandeja,
  BotaoResultados,
  CampoBusca,
  Conteudo,
  ConviteDoMapa,
  LarguraTotalDoMapa,
  Nota,
  Pagina,
  Resultados,
  TituloCompacto,
} from "./styles";
import useCenaDoMapa from "./useCenaDoMapa";
import useDesenhoDoMapa from "./useDesenhoDoMapa";
import usePulsos, { useMovimentosDaPlanilha } from "./usePulsos";

const ROTA_PLANILHAS = "/configuracoes/planilhas";
const CONSULTA_ESTREITA = "(max-width: 767px)";
/**
 * Topo compacto: tablet (dedo, 768 px ou mais) ou qualquer tela baixa
 * (notebook 1366×768). Ali o topo vira duas linhas (ações + números, ~15vh)
 * e os avisos flutuam sobre o mapa — o cabeçalho completo comia metade da
 * altura de um tablet deitado, e o mapa é o conteúdo da tela.
 */
const CONSULTA_TABLET = "(pointer: coarse) and (min-width: 768px), (min-width: 768px) and (max-height: 900px)";
const LIMITE_RESULTADOS = 60;
const PAINEL_BUSCA = "busca";

/** Página do setor (outra tela): tocar numa sala leva para cá. */
export const rotaDoSetor = (setorId) => `/legado/mapa/setor/${encodeURIComponent(setorId)}`;

const dois = (n) => String(n).padStart(2, "0");

function origemDosDados(meta) {
  const quando = meta?.timestamp ? new Date(meta.timestamp) : null;
  if (!quando || Number.isNaN(quando.getTime())) return "Dados da planilha";
  return (
    `Planilha carregada em ${dois(quando.getDate())}/${dois(quando.getMonth() + 1)} ` +
    `${dois(quando.getHours())}:${dois(quando.getMinutes())}`
  );
}

function tituloDoPainel(painel, bandejas, resultados) {
  if (!painel) return { titulo: "", subtitulo: "" };
  if (painel.tipo === PAINEL_BUSCA) {
    return { titulo: "Resultados da busca", subtitulo: plural(resultados.length, "ambiente", "ambientes") };
  }
  if (painel.tipo === SITUACAO.OBRA_FUTURA) {
    return { titulo: "Obra futura", subtitulo: plural(bandejas.obraFutura, "ambiente", "ambientes") };
  }
  const atrasadas = bandejas.semSetorAtrasadas ? ` · ${plural(bandejas.semSetorAtrasadas, "atrasado", "atrasados")}` : "";
  return bandejas.soEscritorio
    ? {
      titulo: "Ainda no escritório",
      subtitulo: `${plural(bandejas.semSetor, "ambiente", "ambientes")} antes da fábrica (medição, projeto, aprovação)${atrasadas}`,
    }
    : {
      titulo: "Sem sala no mapa",
      subtitulo: `${plural(bandejas.semSetor, "ambiente", "ambientes")} em etapas sem sala desenhada${atrasadas}`,
    };
}

/** Nome da bandeja: só escritório, ou tem etapa de fábrica sem sala nenhuma. */
export function rotuloDaBandeja(bandejas, { curto = false } = {}) {
  if (curto) return bandejas.soEscritorio ? "Escritório" : "Sem sala";
  return bandejas.soEscritorio ? "Ainda no escritório" : "Sem sala no mapa";
}

// Id da pessoa no mapa: "<bancada>|<colaborador>" (a mesma pessoa pode estar em duas bancadas).
const colaboradorDoAlvo = (id) => String(id || "").slice(String(id || "").lastIndexOf("|") + 1);

function resolverPopover(popover, cena) {
  if (!popover) return null;
  if (popover.tipo === "ambiente") {
    const demanda = cena.combinadas.find((d) => d.chaveLinha === popover.id);
    return demanda ? { ...popover, demanda, titulo: demanda.rotulo || "Ambiente" } : null;
  }
  if (popover.tipo === "peca") {
    const peca = cena.pecas.find((p) => p.id === popover.id);
    return peca ? { ...popover, peca, titulo: peca.nome || peca.codigo || "Peça" } : null;
  }
  if (popover.tipo === "pessoa") {
    const situacao = cena.situacoes.get(colaboradorDoAlvo(popover.id));
    return situacao ? { ...popover, situacao, titulo: situacao.colaborador.nome } : null;
  }
  const mais = cena.planta.mais.find((m) => m.id === popover.id);
  if (!mais) return null;
  const total = mais.ocultas.length + (mais.ocultasPecas?.length || 0);
  return { ...popover, mais, titulo: `Mais ${plural(total, "item", "itens")}` };
}

/**
 * Modo Organizar. O rascunho é um MAPA setorId → forma (não uma cópia da lista
 * de salas): assim uma sala criada no meio do Organizar entra no desenho na
 * hora, sem descartar o que o gestor já tinha arrastado.
 */
function useOrganizar(salvos, desenho, estado) {
  const [rascunho, setRascunho] = useState(null); // Map<setorId, forma> | null
  const [selecionado, setSelecionado] = useState(null);
  const [desenhando, setDesenhando] = useState(null); // {setorId?} | null
  // O rascunho também vive numa ref para o histórico do Desfazer ser montado
  // fora dos updaters do React (que o StrictMode chama duas vezes).
  const rascunhoRef = useRef(null);
  const historicoRef = useRef([]);
  const [passos, setPassos] = useState(0);
  // Um arrasto dispara dezenas de `definirForma`: o passo do Desfazer é o
  // GESTO inteiro, marcado quando ele começa.
  const passoAbertoRef = useRef(false);

  const elementos = useMemo(() => {
    if (!rascunho || rascunho.size === 0) return salvos;
    return salvos.map((elemento) =>
      rascunho.has(elemento.setorId) ? { ...elemento, forma: rascunho.get(elemento.setorId) } : elemento
    );
  }, [salvos, rascunho]);

  const aplicarRascunho = useCallback((mapa) => {
    rascunhoRef.current = mapa;
    setRascunho(mapa);
  }, []);

  const guardarHistorico = useCallback((lista) => {
    historicoRef.current = lista;
    setPassos(lista.length);
  }, []);

  /** Abre um passo de Desfazer: a próxima mudança de forma guarda o estado atual. */
  const marcarPasso = useCallback(() => { passoAbertoRef.current = true; }, []);

  const definirForma = useCallback((setorId, forma) => {
    const atual = rascunhoRef.current || new Map();
    if (passoAbertoRef.current) {
      passoAbertoRef.current = false;
      guardarHistorico([...historicoRef.current, new Map(atual)]);
    }
    aplicarRascunho(new Map(atual).set(setorId, forma));
  }, [aplicarRascunho, guardarHistorico]);

  const desfazer = useCallback(() => {
    const historico = historicoRef.current;
    if (historico.length === 0) return;
    passoAbertoRef.current = false;
    aplicarRascunho(historico[historico.length - 1]);
    guardarHistorico(historico.slice(0, -1));
  }, [aplicarRascunho, guardarHistorico]);

  const comecar = useCallback(() => {
    guardarHistorico([]);
    aplicarRascunho(new Map());
    setSelecionado(null);
    setDesenhando(null);
  }, [aplicarRascunho, guardarHistorico]);
  const sair = useCallback(() => {
    guardarHistorico([]);
    aplicarRascunho(null);
    setSelecionado(null);
    setDesenhando(null);
  }, [aplicarRascunho, guardarHistorico]);

  // O que o gestor desenhou dentro de cada sala anda junto com ela.
  const levados = useMemo(
    () => (rascunho ? levarFilhos(estado, salvos, elementos) : { estado, filhos: [] }),
    [rascunho, estado, salvos, elementos]
  );

  const salvar = useCallback(async () => {
    await desenho.salvarFormasDosSetores([...formasAlteradas(elementos, salvos), ...levados.filhos]);
    sair();
  }, [desenho, elementos, salvos, levados, sair]);

  return {
    organizando: rascunho !== null,
    elementos,
    estadoComFilhos: levados.estado,
    selecionado,
    desenhando,
    podeDesfazer: passos > 0,
    comecar,
    cancelar: () => {
      desenho.limparErro();
      sair();
    },
    salvar,
    definirForma,
    marcarPasso,
    desfazer,
    selecionar: setSelecionado,
    desenharNovo: () => {
      setSelecionado(null);
      setDesenhando({});
    },
    redesenhar: (setorId) => {
      setSelecionado(setorId);
      setDesenhando({ setorId });
    },
    pararDeDesenhar: () => setDesenhando(null),
  };
}

/**
 * Ações do cabeçalho: as duas bandejas (o que ainda não entrou numa sala) e o
 * modo Organizar. Ficam aqui, e não numa faixa própria, para sobrar altura para
 * o desenho — o mapa é o conteúdo da tela.
 */
function AcoesDoCabecalho({
  organizar,
  podeOrganizar,
  bandejas,
  onAbrirBandeja,
  exportacao,
  compacto = false,
  bandejasNaFaixa = false,
}) {
  if (organizar.organizando) {
    return (
      <>
        <SecondaryButton
          type="button"
          aria-pressed={Boolean(organizar.desenhando)}
          onClick={organizar.desenhando ? organizar.pararDeDesenhar : organizar.desenharNovo}
        >
          <FiPenTool aria-hidden="true" />
          {organizar.desenhando ? "Parar de desenhar" : "Novo setor"}
        </SecondaryButton>
        {/* Desfazer passo a passo: mover, redimensionar e redesenhar voltam um
            de cada vez. Antes só havia o Cancelar, que joga fora tudo. */}
        <SecondaryButton type="button" onClick={organizar.desfazer} disabled={!organizar.podeDesfazer}>
          <FiRotateCcw aria-hidden="true" />
          Desfazer
        </SecondaryButton>
        <SecondaryButton type="button" onClick={organizar.cancelar}>Cancelar</SecondaryButton>
        <AsyncButton onClick={organizar.salvar} successMessage="Desenho do mapa salvo." silentError>
          Salvar
        </AsyncButton>
      </>
    );
  }
  // Celular/tablet (≤1024 px): as bandejas viram tiles da faixa de números e
  // "Apontamentos do dia" fica só ícone, na linha da busca.
  if (bandejasNaFaixa) {
    return (
      <>
        <ApontamentosSoIcone>
          <ExportarApontamentos {...exportacao} />
        </ApontamentosSoIcone>
        {podeOrganizar && (
          <SecondaryButton type="button" onClick={organizar.comecar}>Organizar</SecondaryButton>
        )}
      </>
    );
  }
  return (
    <>
      <BotaoBandeja type="button" onClick={() => onAbrirBandeja(SITUACAO.SEM_SETOR)}>
        {rotuloDaBandeja(bandejas, { curto: compacto })}: <strong>{bandejas.semSetor}</strong>
        {bandejas.semSetorAtrasadas ? (
          <span className="atrasados">
            {" · "}
            {compacto
              ? `${bandejas.semSetorAtrasadas} atras.`
              : plural(bandejas.semSetorAtrasadas, "atrasado", "atrasados")}
          </span>
        ) : null}
      </BotaoBandeja>
      <BotaoBandeja type="button" onClick={() => onAbrirBandeja(SITUACAO.OBRA_FUTURA)}>
        Obra futura: <strong>{bandejas.obraFutura}</strong>
      </BotaoBandeja>
      <ExportarApontamentos {...exportacao} rotulo={compacto ? "Apontamentos" : undefined} />
      {podeOrganizar && (
        <SecondaryButton type="button" onClick={organizar.comecar}>Organizar</SecondaryButton>
      )}
    </>
  );
}

/** Barra da sala selecionada no Organizar: o que dá para mudar além da forma. */
function BarraDoSetorSelecionado({ elemento, onEditar, onRedesenhar, onFechar }) {
  if (!elemento) return null;
  return (
    <BarraDeSelecao role="group" aria-label={`Setor ${elemento.nome}`}>
      <span className="nome">{elemento.nome}</span>
      <span className="etapa">{elemento.etapaRotulo || "Sem etapa da planilha"}</span>
      <SecondaryButton type="button" onClick={() => onRedesenhar(elemento.setorId)}>
        <FiPenTool aria-hidden="true" />
        Redesenhar
      </SecondaryButton>
      <SecondaryButton type="button" onClick={() => onEditar(elemento)}>
        <FiEdit3 aria-hidden="true" />
        Nome e etapa
      </SecondaryButton>
      <button type="button" className="fechar" onClick={onFechar}>Fechar</button>
    </BarraDeSelecao>
  );
}

export default function MapaProducao() {
  const navigate = useNavigate();
  const { dados, meta, initialLoading, erro } = useDados() || {};
  const { usuario, hasPermission, request } = useAuth();
  const estreito = useMediaQuery(CONSULTA_ESTREITA);
  const compacto = useMediaQuery(CONSULTA_TABLET);
  // Redesenho mobile (≤1024 px): cabeçalho compacto e bandejas na faixa.
  const telaCompacta = useMediaQuery(mq.tablet);
  const topoCompacto = compacto || telaCompacta;
  const estadoMapa = useEstadoMapa();
  const { filtros, definirFiltro, removerFiltro, substituirFiltros, totalAtivos } = useFiltrosDoMapa();

  const { estado } = estadoMapa;
  const catalogo = useMemo(() => criarCatalogoSetores(estado.setores), [estado.setores]);
  const salvos = useMemo(() => elementosParaDesenho(catalogo.setores), [catalogo.setores]);
  const desenho = useDesenhoDoMapa({
    request,
    aplicarSetor: estadoMapa.aplicarSetor,
    recarregar: estadoMapa.recarregar,
  });
  const organizar = useOrganizar(salvos, desenho, estado);

  const [verMapaNoCelular, setVerMapaNoCelular] = useState(false);
  const [painel, setPainel] = useState(null); // { tipo, aberto }
  const [detalhe, setDetalhe] = useState(null); // { ambiente, lote, row, voltarPara }
  const [popover, setPopover] = useState(null); // { tipo, id, setor, ancora }
  const [formulario, setFormulario] = useState(null); // { modo, setorId, valores, forma, etapaSugerida }

  const cena = useCenaDoMapa({
    dados,
    // No Organizar, os subsetores e postos já vêm levados junto com a sala.
    estado: organizar.estadoComFilhos || estado,
    catalogo,
    filtros,
    totalAtivos,
    elementos: organizar.elementos,
  });
  const movimentos = useMovimentosDaPlanilha(dados, cena.combinadas);
  const pulsos = usePulsos(estadoMapa.mudancas, movimentos);
  const indice = useMemo(() => indexarEstado(estado), [estado]);
  const orfaos = useAmbientesOrfaos(dados, estado);
  const [religando, setReligando] = useState(false);

  const abrirSetor = useCallback((setorId) => navigate(rotaDoSetor(setorId)), [navigate]);
  // A página do setor abre direto na configuração (subsetores e postos).
  const configurarSetor = useCallback(
    (setorId) => navigate(rotaDoSetor(setorId), { state: { secao: "configurar" } }),
    [navigate]
  );
  const fecharPopover = useCallback(() => setPopover(null), []);

  // O painel guarda o último conteúdo mesmo fechado (`aberto: false`): a
  // animação de saída não pisca uma lista vazia.
  const abrirPainel = useCallback((tipo) => setPainel({ tipo, aberto: true }), []);
  const fecharPainel = useCallback(() => setPainel((p) => (p ? { ...p, aberto: false } : p)), []);

  // O drawer de detalhe fica ABAIXO do SlideOverPanel (z-index); por isso o
  // painel fecha ao abrir o detalhe e volta quando o detalhe fecha.
  const abrirDetalhe = useCallback((demanda) => {
    setPopover(null);
    setDetalhe({
      ambiente: demanda.row[F.AMBIENTE],
      lote: demanda.row[F.NUMERO_DO_LOTE] ?? "—",
      row: demanda.row,
      voltarPara: painel?.aberto ? painel : null,
    });
    fecharPainel();
  }, [painel, fecharPainel]);
  const fecharDetalhe = useCallback(() => {
    if (detalhe?.voltarPara) setPainel(detalhe.voltarPara);
    setDetalhe(null);
  }, [detalhe]);

  const aplicarAtalho = useCallback((atalho, ligado) => {
    if (ligado) removerFiltro(atalho.id);
    else definirFiltro(atalho.id, atalho.valor);
  }, [definirFiltro, removerFiltro]);
  // "Limpar" do painel não apaga a busca: ela está à vista na barra.
  const limparMenosBusca = useCallback(
    () => substituirFiltros({ ...criarFiltrosVazios(), busca: filtros.busca }),
    [substituirFiltros, filtros.busca]
  );

  // ------------------------------------------------- cadastro de setores

  const fecharFormulario = useCallback(() => {
    desenho.limparErro();
    setFormulario(null);
  }, [desenho]);

  // Traço corrigido: sala nova pede o formulário; redesenho entra no rascunho
  // e só vai ao backend quando o gestor salva o Organizar, como mover e
  // redimensionar — um caminho só para "mudou a forma".
  const aoDesenhar = useCallback((forma) => {
    const alvo = organizar.desenhando;
    organizar.pararDeDesenhar();
    if (alvo?.setorId) {
      organizar.marcarPasso();
      organizar.definirForma(alvo.setorId, forma);
    } else setFormulario({ modo: "novo", forma });
  }, [organizar]);

  const editarSetor = useCallback((elemento) => {
    setFormulario({
      modo: "editar",
      setorId: elemento.setorId,
      valores: {
        nome: elemento.nome,
        etapa: elemento.etapa || "",
        recebe_da_planilha: elemento.recebeDaPlanilha,
      },
    });
  }, []);

  const criarParaEtapa = useCallback((etapa) => {
    setFormulario({ modo: "novo", etapaSugerida: etapa, forma: formaParaNovoSetor(organizar.elementos) });
  }, [organizar.elementos]);

  const salvarFormulario = useCallback(async (dados) => {
    if (formulario?.modo === "editar") {
      const elemento = organizar.elementos.find((e) => e.setorId === formulario.setorId);
      await desenho.atualizar(formulario.setorId, { ...dados, versao: elemento?.versao ?? 0 });
    } else {
      await desenho.criar({ ...dados, forma: formulario?.forma || formaParaNovoSetor(organizar.elementos) });
    }
    setFormulario(null);
  }, [formulario, desenho, organizar.elementos]);

  const removerSetor = useCallback(async () => {
    const elemento = organizar.elementos.find((e) => e.setorId === formulario?.setorId);
    await desenho.atualizar(formulario.setorId, { ativo: false, versao: elemento?.versao ?? 0 });
    organizar.selecionar(null);
    setFormulario(null);
  }, [formulario, desenho, organizar]);

  /**
   * Sala que NUNCA recebeu trabalho pode ser apagada de vez — é o desfazer de
   * quem desenhou errado. Com ambiente, tarefa, peça, subsetor, posto ou
   * colaborador na história, o que existe é tirar do mapa (desativar), e a
   * linha do tempo continua fazendo sentido. O backend confere a mesma regra.
   */
  const setorVirgem = useMemo(() => {
    const setorId = formulario?.modo === "editar" ? formulario.setorId : null;
    if (!setorId) return false;
    if ((cena.combinadas || []).some((d) => d.setorId === setorId)) return false;
    const aponta = (lista, campo) => (lista || []).some((item) => (item[campo] || null) === setorId);
    return !(
      aponta(estado.subsetores, "setor_id") ||
      aponta(estado.postos, "setor_id") ||
      aponta(estado.tarefas, "setor_id") ||
      aponta(estado.pecas, "setor_id") ||
      aponta(estado.alocacoes, "setor_id") ||
      (estado.colaboradores || []).some((c) => (c.setor_ids || []).includes(setorId))
    );
  }, [formulario, cena.combinadas, estado]);

  const excluirSetorDoMapa = useCallback(async () => {
    const elemento = organizar.elementos.find((e) => e.setorId === formulario?.setorId);
    await desenho.excluir(formulario.setorId, elemento?.versao ?? 0);
    organizar.selecionar(null);
    setFormulario(null);
  }, [formulario, desenho, organizar]);

  if (initialLoading) return <PageLoader />;

  const nomeUsuario = usuario?.nome || usuario?.email || "usuário";
  const semPlanilha = !Array.isArray(dados) || dados.length === 0;
  if (semPlanilha) {
    return (
      <Pagina data-mapa-largura-total>
        <LarguraTotalDoMapa />
        <PageHeader title="Mapa da Produção" subtitle={`Você está como ${nomeUsuario}`} sticky={false} hideCrumb />
        <Conteudo>
          <EmptyState
            tone="error"
            titulo="Sem planilha carregada"
            descricao={
              (erro ? `${erro} ` : "") +
              "O Mapa da Produção lê a mesma planilha do Legado. Carregue-a em " +
              "Configurações › Planilhas para ver onde cada ambiente está."
            }
            acoes={[{ label: "Abrir Configurações › Planilhas", onClick: () => navigate(ROTA_PLANILHAS) }]}
          />
        </Conteudo>
      </Pagina>
    );
  }

  // Desenhar a planta É o cadastro do setor: mesma permissão (`gerir`).
  const podeGerir = hasPermission(PERMISSAO.GERIR);
  // Sem a primeira leitura do estado não se sabem as versões: salvar forma com
  // versão desconhecida é 409 garantido.
  const podeOrganizar = podeGerir && !estreito && !estadoMapa.carregando && !estadoMapa.erro;
  const { organizando } = organizar;
  const buscaAtiva = Boolean(String(filtros.busca || "").trim());
  const resultados = buscaAtiva ? cena.filtrados.demandas : [];
  const mostrarMapa = !estreito || verMapaNoCelular;
  const contextoFiltros = { demandas: cena.combinadas, estado, catalogo };
  const conteudoPopover = organizando ? null : resolverPopover(popover, cena);
  const { titulo: tituloPainel, subtitulo: subtituloPainel } = tituloDoPainel(painel, cena.bandejas, resultados);
  const selecionado = organizar.elementos.find((e) => e.setorId === organizar.selecionado) || null;
  const semSala = organizar.elementos.length === 0;

  const acoesDoCabecalho = (
    <AcoesDoCabecalho
      organizar={organizar}
      podeOrganizar={podeOrganizar}
      bandejas={cena.bandejas}
      onAbrirBandeja={abrirPainel}
      exportacao={{ request, catalogo, hoje: cena.hoje }}
      compacto={compacto}
      bandejasNaFaixa={telaCompacta}
    />
  );

  // Avisos do topo. No tablet eles flutuam sobre o mapa (AvisosSobreOMapa)
  // em vez de empurrá-lo para baixo.
  const avisos = (
    <>
      {organizando && (
        <AvisoOrganizar>
          {organizar.desenhando
            ? organizar.desenhando.setorId
              ? "Risque o novo contorno desta sala. Ao soltar, o traçado é endireitado."
              : "Risque o contorno da sala nova. Ao soltar, o traçado é endireitado e você dá o nome."
            : "Arraste uma sala para mover, use a alça do canto para redimensionar e toque numa sala para "
              + "renomear ou redesenhar. Salve para todos verem o novo desenho."}
        </AvisoOrganizar>
      )}
      {desenho.erro && (
        <AvisoErro role="alert">
          <span>{desenho.erro}</span>
          <SecondaryButton type="button" onClick={() => { desenho.limparErro(); estadoMapa.recarregar(); }}>
            Recarregar o mapa
          </SecondaryButton>
        </AvisoErro>
      )}
      {catalogo.avisos.length > 0 && !organizando && (
        <Nota role="status">
          {`${plural(catalogo.avisos.length, "etapa precisa", "etapas precisam")} de atenção no cadastro dos `
            + "setores (nenhuma sala recebendo, ou mais de uma marcada). Abra o Organizar para ajustar."}
        </Nota>
      )}
      {!organizando && <AvisoDeOrfaos orfaos={orfaos} podeGerir={podeGerir} onAbrir={() => setReligando(true)} />}
    </>
  );

  const acaoDoGrupo = (bloco) => {
    if (!podeGerir || painel?.tipo !== SITUACAO.SEM_SETOR) return null;
    return (
      <button
        type="button"
        className="acao"
        onClick={() => {
          fecharPainel();
          criarParaEtapa(bloco.chave);
        }}
      >
        {`Criar setor para ${bloco.titulo}`}
      </button>
    );
  };

  return (
    <Pagina data-mapa-largura-total>
      <LarguraTotalDoMapa />
      {!topoCompacto && (
        <PageHeader
          title="Mapa da Produção"
          subtitle={`${origemDosDados(meta)} · Você está como ${nomeUsuario}`}
          actions={acoesDoCabecalho}
          sticky={false}
          hideCrumb
        />
      )}
      <Conteudo data-compacto={compacto ? "sim" : undefined}>
        <Barra data-compacto={topoCompacto ? "sim" : undefined}>
          {topoCompacto && (
            <TituloCompacto title={`${origemDosDados(meta)} · Você está como ${nomeUsuario}`}>
              <h1>Mapa da Produção</h1>
              <span>Você está como {nomeUsuario}</span>
            </TituloCompacto>
          )}
          <CampoBusca>
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={filtros.busca}
              onChange={(e) => definirFiltro("busca", e.target.value)}
              placeholder="Buscar cliente, pedido, lote, peça ou pessoa"
              aria-label="Buscar no mapa"
            />
          </CampoBusca>
          <FiltrosDoMapa
            filtros={filtros}
            definirFiltro={definirFiltro}
            limparFiltros={limparMenosBusca}
            totalAtivos={totalAtivos}
            contexto={contextoFiltros}
            totais={{ ambientes: cena.filtrados.demandas.length, tarefas: cena.filtrados.tarefas.length }}
          />
          {buscaAtiva && (
            <BotaoResultados type="button" onClick={() => abrirPainel(PAINEL_BUSCA)}>
              {resultados.length ? `Ver ${plural(resultados.length, "resultado", "resultados")}` : "Nenhum resultado"}
            </BotaoResultados>
          )}
          {/* No tablet os filtros ativos ficam no contador do botão Filtros:
              os chips abririam uma linha a mais em cima do mapa. */}
          {!topoCompacto && (
            <ChipsDosFiltros filtros={filtros} contexto={contextoFiltros} onRemover={removerFiltro} onLimpar={limparMenosBusca} />
          )}
          <span className="empurra" aria-hidden="true" />
          {estreito && (
            <PrimaryButton type="button" onClick={() => setVerMapaNoCelular((v) => !v)}>
              {verMapaNoCelular ? "Ver lista" : "Ver mapa"}
            </PrimaryButton>
          )}
          {topoCompacto && acoesDoCabecalho}
        </Barra>

        <FaixaIndicadores
          compacta={compacto}
          bandejas={
            telaCompacta && !organizando
              ? {
                rotulo: rotuloDaBandeja(cena.bandejas, { curto: true }),
                rotuloLongo: rotuloDaBandeja(cena.bandejas),
                semSetor: cena.bandejas.semSetor,
                semSetorAtrasadas: cena.bandejas.semSetorAtrasadas,
                obraFutura: cena.bandejas.obraFutura,
                onAbrir: abrirPainel,
              }
              : null
          }
          indicadores={cena.indicadores}
          filtros={filtros}
          onAtalho={aplicarAtalho}
          onAbrirSetor={abrirSetor}
          vivo={<Vivo atualizadoEm={estadoMapa.atualizadoEm} erro={estadoMapa.erro} onRecarregar={estadoMapa.recarregar} />}
        />

        {!compacto && avisos}

        {mostrarMapa ? (
          <MapaSvg
            elementos={organizar.elementos}
            planta={cena.planta}
            resumos={cena.resumos}
            gargalo={cena.gargalo}
            destaque={cena.destaque}
            salasApagadas={cena.salasApagadas}
            situacoes={cena.situacoes}
            tarefasPorPosto={cena.tarefasPorPosto}
            pulsos={pulsos}
            pronto={!estadoMapa.carregando}
            organizando={organizando}
            setorSelecionado={organizar.selecionado}
            desenhando={organizar.desenhando}
            podeConfigurar={podeGerir}
            chaveAjuste={`${salvos.length}:${estadoMapa.carregando ? "carregando" : "pronto"}`}
            onAbrirSetor={abrirSetor}
            onTocar={setPopover}
            onConfigurar={configurarSetor}
            onGesto={fecharPopover}
            onFormaDoSetor={organizar.definirForma}
            onIniciarMudanca={organizar.marcarPasso}
            onSelecionar={organizar.selecionar}
            onRedesenhar={organizar.redesenhar}
            onDesenhado={aoDesenhar}
            previa={formulario?.modo === "novo" ? formulario.forma : null}
          >
            {compacto && <AvisosSobreOMapa>{avisos}</AvisosSobreOMapa>}
            {semSala && !organizar.desenhando && (
              <ConviteDoMapa role="status">
                <strong>Nenhum setor desenhado no mapa</strong>
                <p>
                  Cada sala do mapa é um setor ligado a uma etapa do gerenciador de produção. Enquanto não
                  houver nenhuma, os ambientes ficam na bandeja “Sem sala no mapa”.
                </p>
                {podeGerir && !estreito && (
                  <PrimaryButton
                    type="button"
                    onClick={() => {
                      if (!organizando) organizar.comecar();
                      organizar.desenharNovo();
                    }}
                  >
                    <FiPenTool aria-hidden="true" />
                    Desenhar a primeira sala
                  </PrimaryButton>
                )}
              </ConviteDoMapa>
            )}
            {organizando && selecionado && !organizar.desenhando && (
              <BarraDoSetorSelecionado
                elemento={selecionado}
                onEditar={editarSetor}
                onRedesenhar={organizar.redesenhar}
                onFechar={() => organizar.selecionar(null)}
              />
            )}
            {conteudoPopover && (
              <PopoverMapa ancora={conteudoPopover.ancora} titulo={conteudoPopover.titulo} onFechar={fecharPopover}>
                {conteudoPopover.tipo === "ambiente" && (
                  <ConteudoAmbiente
                    demanda={conteudoPopover.demanda}
                    pecasFora={pecasForaDoAmbiente(cena.pecas, conteudoPopover.demanda.chave)}
                    indice={indice}
                    onAbrirSetor={abrirSetor}
                    onDetalhes={abrirDetalhe}
                    onEscolherPeca={(peca) => setPopover({ ...popover, tipo: "peca", id: peca.id })}
                  />
                )}
                {conteudoPopover.tipo === "peca" && (
                  <ConteudoPeca
                    peca={conteudoPopover.peca}
                    indice={indice}
                    onAbrirSetor={abrirSetor}
                    onAbrirAmbiente={(demanda) => setPopover({ ...popover, tipo: "ambiente", id: demanda.chaveLinha })}
                  />
                )}
                {conteudoPopover.tipo === "pessoa" && (
                  <ConteudoPessoa
                    situacao={conteudoPopover.situacao}
                    setorId={conteudoPopover.setor}
                    nomeDoSetor={catalogo.nome(conteudoPopover.setor)}
                    indice={indice}
                    onAbrirSetor={abrirSetor}
                  />
                )}
                {conteudoPopover.tipo === "mais" && (
                  <ConteudoMais
                    mais={conteudoPopover.mais}
                    nomeDoSetor={catalogo.nome(conteudoPopover.mais.setorId)}
                    onEscolher={(d) => setPopover({ ...popover, tipo: "ambiente", id: d.chaveLinha })}
                    onEscolherPeca={(peca) => setPopover({ ...popover, tipo: "peca", id: peca.id })}
                    onAbrirSetor={abrirSetor}
                  />
                )}
              </PopoverMapa>
            )}
          </MapaSvg>
        ) : (
          <ListaDeSetores
            elementos={organizar.elementos}
            resumos={cena.resumos}
            gargalo={cena.gargalo}
            salasApagadas={cena.salasApagadas}
            onAbrir={abrirSetor}
          />
        )}
      </Conteudo>

      <PainelDeOrfaos
        aberto={religando && podeGerir}
        onFechar={() => setReligando(false)}
        orfaos={orfaos}
        linhas={dados}
        estado={estado}
        request={request}
        onReligado={estadoMapa.recarregar}
      />
      <SlideOverPanel open={Boolean(painel?.aberto)} onClose={fecharPainel} title={tituloPainel} subtitle={subtituloPainel}>
        {painel?.tipo === PAINEL_BUSCA ? (
          <Resultados aria-label="Resultados da busca">
            {resultados.length > LIMITE_RESULTADOS && <Nota>{`Mostrando ${LIMITE_RESULTADOS} de ${resultados.length}.`}</Nota>}
            {resultados.length === 0 && <Nota>Nenhum ambiente encontrado.</Nota>}
            {resultados.slice(0, LIMITE_RESULTADOS).map((demanda) => (
              <CartaoDemanda key={demanda.chaveLinha} demanda={demanda} mostrarLocal onAbrir={abrirDetalhe} />
            ))}
          </Resultados>
        ) : (
          <ListaDemandas
            blocos={painel ? gruposDaBandeja(cena.combinadas, painel.tipo) : []}
            onAbrir={abrirDetalhe}
            acaoDoBloco={acaoDoGrupo}
            vazio="Nenhum ambiente aqui agora."
          />
        )}
      </SlideOverPanel>

      <FormularioSetor
        aberto={Boolean(formulario)}
        modo={formulario?.modo || "novo"}
        setorId={formulario?.setorId || null}
        valores={formulario?.valores || null}
        etapaSugerida={formulario?.etapaSugerida || ""}
        catalogo={catalogo}
        erro={desenho.erro}
        onFechar={fecharFormulario}
        onSalvar={salvarFormulario}
        onRemover={formulario?.modo === "editar" ? removerSetor : null}
        onExcluir={formulario?.modo === "editar" && setorVirgem ? excluirSetorDoMapa : null}
      />

      <AmbienteDetalhesDrawer ambiente={detalhe} onClose={fecharDetalhe} />
    </Pagina>
  );
}
